import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Marquee } from '@sorare/core/src/atoms/container/Marquee';
import { SorareLogo } from '@sorare/core/src/atoms/icons/SorareLogo';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { DisplayS } from '@sorare/core/src/atoms/typography';
import { Img } from '@sorare/core/src/atoms/ui/Img';
import { FOOTBALL_BESTOFEUROPE } from '@sorare/core/src/constants/__generated__/routes';
import { useConnectionContext } from '@sorare/core/src/contexts/connection';
import { useIsDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsDesktopAndAbove';
import { desktopAndAbove } from '@sorare/core/src/style/mediaQuery';

import bundesligaMobileAvif from './assets/bundesliga-mobile.avif';
import bundesligaMobile from './assets/bundesliga-mobile.png';
import bundesligaAvif from './assets/bundesliga.avif';
import bundesliga from './assets/bundesliga.jpg';
import leaguesAvif from './assets/leagues.avif';
import leagues from './assets/leagues.png';
import logoMobile from './assets/logo-mobile.svg';
import logo from './assets/logo.svg';
import mobileAvif from './assets/mobile.avif';
import mobile from './assets/mobile.jpg';
import ribonBackAvif from './assets/ribon-back.avif';
import ribonBack from './assets/ribon-back.jpg';
import ribonAvif from './assets/ribon.avif';
import ribon from './assets/ribon.jpg';

const Root = styled.section`
  position: relative;
  isolation: isolate;
`;
const Grid = styled.div`
  position: relative;
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  padding: var(--quadruple-unit) 0 0;
  background-image: url(${mobile}});
  background-image: image-set(
    url(${mobileAvif}) type('image/avif'),
    url(${mobile})
  );
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  margin-bottom: var(--double-unit);
  &:before {
    position: absolute;
    pointer-events: none;
    content: '';
    inset: 0;
    background-image: linear-gradient(
        to top,
        var(--c-neutral-100),
        transparent 16%
      ),
      linear-gradient(to bottom, var(--c-neutral-100), transparent 16%);
    z-index: 1;
  }
  @media ${desktopAndAbove} {
    display: grid;
    justify-content: center;
    gap: var(--double-unit);
    grid-template-areas: '. content';
    grid-template-columns: minmax(0, 725px) 536px;
    min-height: 810px;
    background-image: url(${bundesliga});
    background-image: image-set(
      url(${bundesligaAvif}) type('image/avif'),
      url(${bundesliga})
    );
    margin-bottom: 0;
  }
`;
const MarqueeContent = styled(Horizontal).attrs({ gap: 0 })`
  height: 50px;
`;

const Content = styled(Vertical)`
  grid-area: content;
  align-items: center;
  z-index: 1;
  text-align: center;
  @media ${desktopAndAbove} {
    align-items: flex-start;
    text-align: left;
    padding: var(--double-unit);
  }
`;
const Ribbon = styled.div`
  position: relative;
  z-index: 2;
  font-family: var(--rigid-square);
  font-size: 43px;
  letter-spacing: 0.08em;
  font-weight: bold;
  transform: rotate(-0.7deg);
  text-transform: uppercase;
  &::before,
  &::after {
    position: absolute;
    inset: 0;
    z-index: -1;
    content: '';
  }
  &:before {
    transform: rotate(-1deg);
    background-image: image-set(
      url(${ribonBackAvif}) type('image/avif'),
      url(${ribonBack})
    );
  }
  &:after {
    background-image: image-set(
      url(${ribonAvif}) type('image/avif'),
      url(${ribon})
    );
  }
`;
const Picture = styled.picture`
  position: relative;
  &:before {
    position: absolute;
    pointer-events: none;
    content: '';
    inset: 0 0 -1px 0;
    background-image: linear-gradient(
      to top,
      var(--c-neutral-100),
      transparent 16%
    );
    z-index: 1;
  }
  img {
    width: 100%;
  }
`;
const Description = styled(DisplayS)`
  @media ${desktopAndAbove} {
    max-width: 80%;
  }
`;
const Buttons = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: var(--double-unit);
  justify-content: center;
  @media ${desktopAndAbove} {
    justify-content: start;
    flex-direction: row;
  }
`;

export const BestOfEuropeBanner = () => {
  const { signUp } = useConnectionContext();
  const isLaptop = useIsDesktopAndAbove();
  const description = (
    <>
      <Description>
        <FormattedMessage
          id="landing.EarlyAccess.BestOfEuropeBanner.desc"
          defaultMessage="The Best of Europe Competitions"
        />
      </Description>
      <Buttons>
        <Button color="secondary" size="large" onClick={signUp}>
          <FormattedMessage
            id="landing.EarlyAccess.BestOfEuropeBanner.enter"
            defaultMessage="Enter competition"
          />
        </Button>
        <Button color="transparent" size="large" to={FOOTBALL_BESTOFEUROPE}>
          <FormattedMessage
            id="landing.EarlyAccess.BestOfEuropeBanner.learn"
            defaultMessage="Learn more"
          />
        </Button>
      </Buttons>
    </>
  );
  return (
    <Root>
      <Ribbon>
        <Marquee direction="reverse" duration="10s">
          <MarqueeContent>
            <SorareLogo />
          </MarqueeContent>
        </Marquee>
      </Ribbon>
      <Grid>
        <Content gap={3}>
          {isLaptop && (
            <picture>
              <source srcSet={leaguesAvif} type="image/avif" />
              <Img src={leagues} alt="" width="212" height="39" />
            </picture>
          )}
          <picture>
            <source
              srcSet={logo}
              media={desktopAndAbove}
              width="800"
              height="77"
            />
            <Img src={logoMobile} alt="" width="285" height="108" />
          </picture>
          {!isLaptop && (
            <picture>
              <source srcSet={leaguesAvif} type="image/avif" />
              <Img src={leagues} alt="" width="212" height="39" />
            </picture>
          )}
          {!isLaptop && (
            <Picture>
              <source srcSet={bundesligaMobileAvif} type="image/avif" />
              <Img
                src={bundesligaMobile}
                fetchpriority="low"
                width="794"
                height="671"
              />
            </Picture>
          )}
          {isLaptop && description}
        </Content>
      </Grid>
      {!isLaptop && (
        <Vertical gap={3} center className="text-center">
          {description}
        </Vertical>
      )}
    </Root>
  );
};
